import i18next from 'i18next';
import moment from 'moment';
import 'moment-duration-format';
import {isType1} from './IsType1';
import {isType234} from './ItType234';
import {Plural} from './Strings';

// checks if string is a valid duration like 1w 2d 3h 5m
export function IsValidDuration(str: string): boolean {
    if (str.length === 0) {
        return false;
    }

    return /^(\d{1,}w){0,}\s{0,}(\d{1,}d){0,}\s{0,}(\d{1,}h){0,}\s{0,}(\d{1,}m){0,}\s{0,}$/g.test(
        str.trim().toLowerCase()
    );
}

export function StrDurationToSeconds(str: string): number {
    if (!IsValidDuration(str)) {
        return 0;
    }

    const regexp = new RegExp(/(\d{1,}w)|(\d{1,}d)|(\d{1,}h)|(\d{1,}m)/g);
    const matches = str.match(regexp);

    let duration = 0;
    matches?.forEach(match => {
        let multiplier = 0;
        switch (match[match.length - 1]) {
            case 'w':
                multiplier = 7 * 24 * 3600;
                break;
            case 'd':
                multiplier = 24 * 3600;
                break;
            case 'h':
                multiplier = 3600;
                break;
            case 'm':
                multiplier = 60;
                break;
            default:
                break;
        }

        duration += multiplier * parseInt(match);
    });

    return duration;
}

export function SecondsToStrDuration(seconds: number, template: string): string {
    if (seconds === 0) {
        return 'N/A';
    }

    return moment.duration(seconds, 'seconds').format({
        // EXAMPLES:
        // w[w] d[d] h[h] m[m] => 3w 2d 3h 45m
        // h [hours] m [minutes] => 3 hours 21 minutes
        template,
        trim: 'both',
    });
}

/**
 * @param seconds duration in seconds
 * @param format printing format (e.g. "precise" - 3 days 1 hour 3 minutes, 3 hours 1 minute; "approx" - ~ 2 hours, ~ 1 hour)
 */
export function PrintDuration(seconds: number, format: 'approx' | 'precise' | 'daymin'): string {
    if (format === 'precise') {
        return SecondsToStrDuration(seconds, 'd [days] h [hours] m [minutes]');
    } else if (format === 'daymin') {
        return `~ ${getFullDuration(seconds)}`;
    }

    const hours = Math.floor(seconds / 3600 + 1);
    return `~ ${hours} ${getDurationMarker(hours)}`;
}

export const getDurationMarker = (hours: number) => {
    switch (i18next.language) {
        case 'ru':
            if (isType1(hours)) return 'час';
            if (isType234(hours)) return 'часа';
            return 'часов';
        case 'de':
            return 'Std.';
        case 'nl':
            return 'uur.';
        case 'it':
            return hours > 1 ? 'ore' : 'ora';
        case 'fr':
            return Plural('heure', hours);
        case 'es':
            return Plural('hora', hours);
    }

    return Plural('hour', hours);
};

export const getFullDuration = (totalSeconds: number) => {
    let dayMarker, hourMarker;

    switch (i18next.language) {
        case 'ru':
            dayMarker = (days: number) => (days === 1 ? 'день' : isType234(days) ? 'дня' : 'дней');
            hourMarker = (hours: number) => (hours === 1 ? 'час' : isType234(hours) ? 'часа' : 'часов');
            break;
        case 'de':
            dayMarker = () => 'Tage';
            hourMarker = () => 'Std.';
            break;
        case 'nl':
            dayMarker = () => 'dagen';
            hourMarker = () => 'uur.';
            break;
        case 'it':
            dayMarker = (days: number) => (days === 1 ? 'giorno' : 'giorni');
            hourMarker = (hours: number) => (hours === 1 ? 'ora' : 'ore');
            break;
        case 'fr':
            dayMarker = (days: number) => Plural('jour', days);
            hourMarker = (hours: number) => Plural('heure', hours);
            break;
        case 'es':
            dayMarker = (days: number) => Plural('día', days);
            hourMarker = (hours: number) => Plural('hora', hours);
            break;
        default:
            dayMarker = (days: number) => Plural('day', days);
            hourMarker = (hours: number) => Plural('hour', hours);
    }

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);

    const parts = [];
    if (days > 0) parts.push(`${days} ${dayMarker(days)}`);
    if (hours > 0) parts.push(`${hours} ${hourMarker(hours)}`);

    return parts.join(' ') || `0 ${hourMarker(0)}`;
};
